<template>
  <i-row>
    <i-row>
      <AgGridVue
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        style="width: 100%; height: 640px;"
        class="ag-theme-balham"
        :rowData="dataset"
        :animateRows="true"
        :sideBar="true"
        @gridReady="onGridReady"
        domLayoutDisable="autoHeight"
      ></AgGridVue>
    </i-row>
    <br />
    <i-row>
      <i-button @click="exportExcel(true)" icon="md-download">Download Sub-Facts</i-button>&nbsp;
    </i-row>
    <br />
  </i-row>
</template>

<script>
import axios from "axios";
import Excel from "@/models/Excel.ts";
import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { AgGridVue } from "ag-grid-vue";
import CopyButton from "@/grid-components/copy-button.vue";
import LinkComponent from "@/grid-components/link.vue";
import TagsComponent from "@/grid-components/tags.vue";

export default {
  name: "SubFactsTable",
  components: { AgGridVue, CopyButton, LinkComponent },
  props: ["hack", "tabIndex", "factID"],
  data() {
    return {
      dataset: [],
      gridOptions: {
        sideBar: {
          toolPanels: ["columns", "filters"],
          defaultToolPanel: "",
        },
        defaultColDef: {
          sortable: true,
          resizable: true,
          filter: true,
        },
        enableRangeSelection: true,
        allowContextMenuWithControlKey: true,
        getContextMenuItems: params => [
          {
            name: "Copy URL",
            action: () => {
              // console.log(params.node.data);
              this.copyClipboard(params.node.data);
            },
          },
          "separator",
          ...params.defaultItems,
        ],
      },
      columnDefs: Object.freeze([
        {
          suppressMenu: true,
          resizable: false,
          headerName: "IDU",
          field: "idu",
          width: 50,
          enableRowGroup: true,
          cellRenderer: params => {
            if (params.node.data) {
              return params.node.data.idu
                ? '<i class="fa fa-check-circle"></i>'
                : "";
            }
          },
        },
        {
          headerName: "Name",
          field: "name",
          width: 250,
          cellRendererFramework: LinkComponent,
          cellRendererParams: {
            link: data => `/facts/${data.id}`,
            name: data => data.name,
          },
        },
        { headerName: "Country", field: "country", enableRowGroup: true },
        {
          headerName: "Figure",
          field: "figure",
          valueFormatter: ({ value }) => {
            if (!!value) {
              return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
            } else {
              return "";
            }
          },
          width: 90,
        },
        { headerName: "Type", field: "type", enableRowGroup: true },
        {
          headerName: "Groups",
          field: "groups",
          cellRendererFramework: TagsComponent,
          cellRendererParams: {
            items: data => data.groups || [],
          },
        },
        { headerName: "Year", field: "year", width: 70, enableRowGroup: true },
        {
          headerName: "Start Date",
          field: "start_date",
          valueFormatter: params =>
            params.data.type.includes("Flow") ? params.data.stock_date : "",
          width: 120,
        },
        {
          headerName: "Cause",
          field: "displacement_type",
          width: 90,
          enableRowGroup: true,
        },
        {
          headerName: "Class",
          field: "class",
          width: 70,
          enableRowGroup: true,
        },
        {
          headerName: "Role (Event)",
          field: "event_role",
          enableRowGroup: true,
        },
        {
          headerName: "Role (Master)",
          field: "parent_role",
          enableRowGroup: true,
        },
        {
          headerName: "Event Name",
          field: "event_name",
          width: 250,
          cellRendererFramework: LinkComponent,
          cellRendererParams: {
            link: data => `/events/${data.event_id}`,
            name: data => data.event_name,
          },
        },
        {
          headerName: "Hazard Subtype",
          field: "hazard_subtype",
          enableRowGroup: true,
        },
        {
          headerName: "Created by",
          field: "created_by",
          enableRowGroup: true,
        },
        {
          headerName: "Region",
          field: "region",
          enableRowGroup: true,
          hide: true,
        },
        {
          headerName: "Stock Date",
          field: "stock_date",
          valueFormatter: params =>
            params.data.type.includes("Stock") ? params.data.stock_date : "",
          width: 120,
          hide: true,
        },
        {
          headerName: "End Date",
          field: "end_date",
          valueFormatter: params =>
            params.data.type.includes("Flow") ? params.data.stock_date : "",
          width: 120,
          hide: true,
        },
        {
          headerName: "Public Figure Analysis",
          field: "public_figure_analysis",
          width: 120,
          hide: true,
        },
        { headerName: "GSN", field: "gsn", width: 140, hide: true },
        {
          headerName: "Status",
          field: "status",
          enableRowGroup: true,
          hide: true,
        },
        { headerName: "Qualifier", field: "qualifier", hide: true },
        {
          headerName: "Sources",
          field: "sources",
          cellRendererFramework: TagsComponent,
          cellRendererParams: {
            items: data => data.sources || [],
          },
          hide: true,
        },
        { headerName: "Created at", field: "created_at", hide: true },
      ]),
    };
  },

  watch: {
    async hack(value) {
      if (value === this.tabIndex) {
        this.gridOptions.api.showLoadingOverlay();
        await this.load();
        this.gridOptions.api.hideOverlay();
        this.gridOptions.columnApi.autoSizeColumns();
      } else {
        this.dataset = [];
      }
    },
  },

  methods: {
    async load() {
      const url = `/psql/facts_view?parent_id=eq.${this.factID}`;
      let response = await axios.get(url);
      this.dataset = response.data;
    },

    onGridReady(params) {
      this.gridOptions.api.closeToolPanel();
      this.gridOptions.getContextMenuItems = contextParams => [
        {
          name: "Copy URL",
          action: () => {
            this.copyClipboard(contextParams.node.data);
          },
        },
        "separator",
        ...contextParams.defaultItems,
      ];
    },

    copyClipboard(data) {
      const link = `${window.origin}/facts/${data.id}`;
      this.$copyText(link).then(
        () => this.$Message.success("Link copied to clipboard"),
        () => this.$Message.error("Can't copy link")
      );
    },

    async exportExcel() {
      const url = `/psql/facts_download_view?parent_id=eq.${this.factID}`;
      let response = await axios.get(url);
      let dataset = response.data;
      const sample = dataset[0];

      if (sample) {
        const filename = `fact_${this.factID}_sub_facts.xlsx`;
        const excelFile = new Excel();
        const headers = Object.keys(sample);
        excelFile
          .addSheet("Sub Facts", headers, headers, dataset)
          .download(filename);
      }
    },

    icon(value) {
      switch (value) {
        case "Under Review":
          return "search";
        case "Review Completed":
          return "check";
        case "Signed Off":
          return "pencil";
        case "Locked Down":
          return "lock";
        default:
          return "";
      }
    },
  },
};
</script>

<style lang="css">
.ivu-table-small td {
  height: 32px;
}

.comment {
  color: #5c738e;
}
.search {
  color: #dc722d;
}
.check {
  color: #748a5a;
}
.pencil {
  color: #50a090;
}
.lock {
  color: #296ca2;
}
.hash {
  color: gray;
}
</style>
